import { z } from 'zod'
import { ArchivableSchema } from '../archive'
import { AuditPropsSchema } from '../common'
import { CustomFieldIdSchema } from '../customField/id'
import { AccountStatusIdSchema, TeamIdSchema } from '../ids'
import { PERMISSION_DEFINITION } from '../permissions'
import { RequirableResidentFieldSchema } from '../resident'
import { RoleIdSchema } from '../role'
import { IOTypeSchema } from '../widget/shared'

export const ACCOUNT_STATUS_CODES = [
  'Lead',
  'Set',
  'Sit',
  'Closed',
  'Approved',
  'Permit Submitted',
  'Permit Approved',
  'Installed',
  'Archived',
  'Do Not Contact',
] as const
export const AccountStatusCodeSchema = z.enum(ACCOUNT_STATUS_CODES)
/** Will eventually be replaced by AccountStatusId */
export type AccountStatusCode = z.infer<typeof AccountStatusCodeSchema>

export const RequiredAccountFieldSchema = z.union([RequirableResidentFieldSchema, z.lazy(() => CustomFieldIdSchema)])
export type RequiredAccountField = z.infer<typeof RequiredAccountFieldSchema>
export const isRequiredAccountField = (value: unknown): value is RequiredAccountField => {
  return RequiredAccountFieldSchema.safeParse(value).success
}

export const SetterCloserConfigSchema = z.object({
  includeOnPlan: z.boolean().optional().meta({ description: 'Whether to include this on quarterly plans' }),
  allowPayment: z.boolean().optional().meta({ description: 'Whether to allow payment calculation on quarterly plans' }),
})
export type SetterCloserConfig = z.infer<typeof SetterCloserConfigSchema>

const ACCOUNT_STATUS_SCOPES = PERMISSION_DEFINITION['account:list'].entities
export const AccountStatusScopeSchema = z.enum(ACCOUNT_STATUS_SCOPES)
export type AccountStatusScope = z.infer<typeof AccountStatusScopeSchema>
export const isAccountStatusScope = (value: unknown): value is AccountStatusScope => {
  return AccountStatusScopeSchema.safeParse(value).success
}

export const UnsavedAccountStatusSchema = z.object({
  name: z.string().meta({ description: 'Label or name for a single account in this status' }),
  order: z.number().int().optional().meta({ description: 'Order of the status in the workflow', deprecated: true }),
  short: z.string().optional().meta({ description: 'Short version of the name, such as an abbreviation' }),
  plural: z.string().optional().meta({ description: 'Plural version of this status if any' }),
  description: z.string().optional().meta({ description: 'Optional description of this status' }),
  legacyCode: AccountStatusCodeSchema.optional(),
  color: z.string().optional().meta({ description: 'Color associated with the status' }),
  icon: z.string().optional().meta({ description: 'Icon associated with the status' }),
  restricted: z.boolean().optional().meta({
    description: 'If true, restrict prospecting for accounts with this statusId',
  }),
  roles: z
    .record(RoleIdSchema, SetterCloserConfigSchema)
    .optional()
    .meta({ description: 'Roles associated with the status' }),
  integrationMapping: z.array(z.string()).optional().meta({
    description: 'Integration mapping for the status',
  }),
  scope: AccountStatusScopeSchema,
  setter: SetterCloserConfigSchema.optional().meta({ description: 'Configuration for reps who are setters' }),
  closer: SetterCloserConfigSchema.optional().meta({ description: 'Configuration for reps who are closers' }),
  selfgen: SetterCloserConfigSchema.optional().meta({ description: 'Configuration for selfgen' }),
  isFinalState: z.boolean().optional().meta({
    description: 'Does this account stage represent a final state in the account workflow?',
  }),
  createUser: z.boolean().optional().meta({
    description:
      'When enabled, a user account is automatically created for the recruit once they reach this stage of the recruiting workflow.',
  }),
  columnVisibility: z.array(TeamIdSchema).optional().meta({
    description: "If specified, the status will only appear in the specified team's downline",
  }),
  ioType: z.lazy(() => IOTypeSchema).optional(),
  requiredFields: z.array(RequiredAccountFieldSchema).optional().meta({
    description: 'Fields required for this account status',
  }),
  isArchivedState: z.boolean().optional().meta({
    description:
      'when enabled, user on the account will be archived when account reaches this status of the recruiting workflow',
  }),
})

export type UnsavedAccountStatus = z.infer<typeof UnsavedAccountStatusSchema>

export const AccountStatusDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  ...UnsavedAccountStatusSchema.shape,
  statusId: AccountStatusIdSchema,
})
export type AccountStatusData = z.infer<typeof AccountStatusDataSchema>

export const TinyAccountStatusSchema = AccountStatusDataSchema.pick({
  name: true,
  statusId: true,
})
export type TinyAccountStatus = z.infer<typeof TinyAccountStatusSchema>
